import { Box, Flex, HStack, IconButton, Text } from '@chakra-ui/react'
import { LuCheck, LuRectangleVertical, LuRotateCw, LuStar, LuX } from 'react-icons/lu'
import { watchGraceMs } from '../../constants/moderation'
import { useTranslation } from '../../i18n'
import { VideoSurface } from './VideoSurface'

// One grid slot.
//
// The slot, not the video, is the unit: a decided video keeps its tile, its
// position and its highlighted button until a newly claimed video needs the
// space. Nothing reflows under a moderator's cursor mid-decision.

function EmptyTile() {
  return (
    <Box
      bg="var(--mc-bg-surface)"
      border="1px dashed var(--mc-border)"
      borderRadius="sm"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Text fontSize="xs" color="#1e293b">
        —
      </Text>
    </Box>
  )
}

export function ModerationTile({
  slot,
  focused,
  onFocus,
  onDecide,
  onFavorite,
  onWatched,
  onRotate,
  onForceVertical,
  showDisplayControls,
  challengeMeta,
  gateWatch,
}) {
  const { t } = useTranslation()
  if (!slot) return <EmptyTile />

  const { video, decision, watched, lost, lostTo } = slot
  const meta = challengeMeta?.[video.challenge]
  // Two reasons the buttons go inert. The gate applies to a first decision on a
  // pending video — re-deciding one already judged is fine, it has been seen.
  // A lost tile is not ours to judge at all: somebody else holds or has already
  // decided that video, and a click here would quietly overturn their call.
  // That is not hypothetical — a moderator coming back from a dropped
  // connection is looking at exactly this tile, and reaches for it.
  const locked = (gateWatch && !watched && !decision) || lost
  const accepted = decision === 'accepted'
  const rejected = decision === 'rejected'

  return (
    <Box
      position="relative"
      bg="black"
      overflow="hidden"
      borderRadius="sm"
      minH={0}
      minW={0}
      /* Stable hooks for the verification harness in UPLOAD_TESTER. Aria labels
         are translated, so they cannot be what a script keys off. */
      data-video-id={video.id}
      data-challenge={video.challenge}
      data-decision={decision || 'none'}
      data-watched={watched ? 'true' : 'false'}
      data-lost={lost ? 'true' : 'false'}
      border={focused ? '2px solid var(--mc-focus)' : '1px solid var(--mc-border)'}
      boxShadow="none"
      onClick={onFocus}
      cursor="pointer"
    >
      <VideoSurface
        src={video.url || video.videoUrl}
        challenge={meta?.label || video.challengeLabel || video.challenge}
        challengeColor={meta?.color}
        challengeTextColor={meta?.textColor}
        trimStart={video.trimStart}
        trimEnd={video.trimEnd}
        rotation={video.rotation}
        forceVertical={video.forceVertical}
        trackWatch={gateWatch && !decision}
        graceMs={watchGraceMs(video)}
        onWatched={onWatched}
      />

      {/* Taken over by someone else, or decided by them, while it was on screen.
          Veiled rather than removed: a tile that vanishes mid-decision is worse
          than one that explains itself. */}
      {lost && (
        <Flex
          position="absolute"
          inset="0"
          align="center"
          justify="center"
          bg="blackAlpha.700"
          pointerEvents="none"
        >
          <Text fontSize="xs" color="var(--mc-text-muted)" fontFamily="'JetBrains Mono', monospace">
            {lostTo ? t('moderation.takenOver') : t('moderation.decidedElsewhere')}
          </Text>
        </Flex>
      )}

      <IconButton
        aria-label={t('moderation.favoriteAria')}
        position="absolute"
        top="3px"
        right="3px"
        rounded="full"
        size="xs"
        variant="ghost"
        color={video.isFavorite ? 'yellow.400' : 'whiteAlpha.600'}
        onClick={(e) => {
          e.stopPropagation()
          onFavorite()
        }}
      >
        <LuStar fill={video.isFavorite ? 'currentColor' : 'none'} />
      </IconButton>

      {showDisplayControls && (
        <HStack position="absolute" bottom="8px" left="6px" gap={1} zIndex={2}>
          <IconButton
            aria-label={t('videosPage.rotate')}
            rounded="full"
            size="xs"
            variant="ghost"
            bg="blackAlpha.500"
            color="white"
            _hover={{ bg: 'blackAlpha.700' }}
            onClick={(e) => {
              e.stopPropagation()
              onRotate()
            }}
          >
            <LuRotateCw />
          </IconButton>
          <IconButton
            aria-label={t('videosPage.forceVertical')}
            rounded="full"
            size="xs"
            variant="ghost"
            bg="blackAlpha.500"
            color={video.forceVertical ? 'blue.300' : 'white'}
            _hover={{ bg: 'blackAlpha.700' }}
            onClick={(e) => {
              e.stopPropagation()
              onForceVertical()
            }}
          >
            <LuRectangleVertical />
          </IconButton>
        </HStack>
      )}

      {/* The decision. The chosen side stays filled and ringed so a decided tile
          is readable at a glance across a wall of them; the other side dims but
          stays clickable, which is the whole undo mechanism. */}
      <Flex
        position="absolute"
        bottom="8px"
        left="0"
        right="0"
        justify="center"
        gap={3}
        pointerEvents="none"
      >
        <IconButton
          aria-label={t('moderation.rejectAria')}
          data-action="reject"
          rounded="full"
          size="sm"
          bg={rejected ? 'red.500' : 'blackAlpha.600'}
          color={accepted ? 'whiteAlpha.500' : 'white'}
          opacity={locked ? 0.35 : accepted ? 0.5 : 1}
          boxShadow={rejected ? '0 0 0 3px rgba(239,68,68,0.45)' : 'none'}
          pointerEvents={locked ? 'none' : 'auto'}
          _hover={{ bg: 'red.400' }}
          onClick={(e) => {
            e.stopPropagation()
            onDecide(false)
          }}
        >
          <LuX />
        </IconButton>
        <IconButton
          aria-label={t('moderation.acceptAria')}
          data-action="accept"
          rounded="full"
          size="sm"
          bg={accepted ? 'green.500' : 'blackAlpha.600'}
          color={rejected ? 'whiteAlpha.500' : 'white'}
          opacity={locked ? 0.35 : rejected ? 0.5 : 1}
          boxShadow={accepted ? '0 0 0 3px rgba(34,197,94,0.45)' : 'none'}
          pointerEvents={locked ? 'none' : 'auto'}
          _hover={{ bg: 'green.400' }}
          onClick={(e) => {
            e.stopPropagation()
            onDecide(true)
          }}
        >
          <LuCheck />
        </IconButton>
      </Flex>
    </Box>
  )
}
